import type { NotepadWidgetModel } from '.';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { NotepadWidgetState } from './NotepadSelfOwnedModel';
import { NotepadSelfOwnedModel, notepadWidgetStateSchema } from './NotepadSelfOwnedModel';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { inject, injectable } from 'src/features/ioc';
import type { ReactContainer } from 'src/ReactContainer';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import type { GetInitialData, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { z } from 'zod';

@injectable()
export class NotepadWidgetModelImpl implements NotepadWidgetModel {
    static readonly stateSchema: z.ZodType<NotepadWidgetState> = notepadWidgetStateSchema;

    static defaultState(): NotepadWidgetState {
        return { content: '' };
    }

    static async fromJSON(container: ReactContainer): Promise<NotepadWidgetModelImpl> {
        const getInitialData = container.get('GetInitialData') as GetInitialData<unknown>;
        const raw = await getInitialData();
        const validated = NotepadWidgetModelImpl.stateSchema.parse(raw);
        container.bind('InitialWidgetState').toConstantValue(validated);
        return container.get('NotepadWidgetModel') as NotepadWidgetModelImpl;
    }

    readonly #model: NotepadSelfOwnedModel;
    readonly #disposableStack = new DisposableStack();
    readonly headerElements: WidgetHeaderElement[];

    constructor(
        @inject('WidgetId') tabId: WidgetId,
        @inject('WidgetHostContainer') container: WidgetHostContainer,
        @inject('InitialWidgetState') initialState: unknown,
        @inject('Logger') logger: Logger,
    ) {
        const persisted = initialState as NotepadWidgetState;

        this.#model = this.#disposableStack.use(
            new NotepadSelfOwnedModel({
                container,
                tabId,
                persisted,
                logger,
            }),
        );

        this.headerElements = [{ type: 'localOnlyInfo', position: 21, alwaysVisible: true }, ...COMMON_HEADER_ELEMENTS];
    }

    get content(): ReadonlySignal<string> {
        return this.#model.content;
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model.dirty;
    }

    get eventBus(): NotepadWidgetModel['eventBus'] {
        // The inner model's `eventBus` is a `MergedEventBus<S1 & S2, E1 & E2>`
        // built from the two Container slices. `createModel`'s `ValidateModel`
        // type erases the specific generics on nested objects (the `getSignal`
        // return type collapses to `ReadonlySignal<unknown>`). Cast to recover
        // the typed shape declared on the {@link NotepadWidgetModel} interface.
        return this.#model.eventBus as NotepadWidgetModel['eventBus'];
    }

    setContent(value: string): void {
        this.#model.setContent(value);
    }

    toJSON(): NotepadWidgetState {
        return this.#model.toJSON();
    }

    markClean(): void {
        this.#model.markClean();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
